import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Checkbox, Divider, FormControlLabel } from '@mui/material';
import { RootState } from 'src/store/store';
import { addPositiveSign, getLabelFallback, range, returnValueIf } from '@utils/utils';
import {
    IAddToCartSavedPrescriptionPayload,
    IContactLensForm,
} from '@interfaces/contactsInterfaces';
import CLDiscounts from '../my-new-prescription/CLDiscounts';
import FormColumn from './FormColumn';
import DifferentProductPrescriptionForm from './DifferentProductPrescriptionForm';
import useOnFormSubmit from '@hooks/useOnFormSubmit';
import useOnFormChange from '@hooks/useOnFormChange';
import { cleanContactLensForm } from '@utils/pages-utils/ContactsUtils';

const SavedPrescriptionForm = (): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);
    const currentProduct = useSelector((state: RootState) => state.contacts.currentProduct);
    const currentPrescription = useSelector(
        (state: RootState) => state.contacts.currentPrescription
    );

    const { leftCl, rightCl } = currentPrescription;

    //console.log('vincenzo', currentPrescription);

    // check if the left and right CL are SKUs of the CL on the current PDP
    const isTwoProducts = !!leftCl && !!rightCl;
    const isSameProduct = leftCl?.parentId === rightCl?.parentId;
    const leftIsCurrentProduct = leftCl?.parentId === currentProduct.productId;
    const rightIsCurrentProduct = rightCl?.parentId === currentProduct.productId;
    const boxesValueOptions = range(1, config.maxBoxes, true).map(String);
    const defaultBoxesValue = config.defaultBoxes.toString();
    let leftIsAvailable = true;
    let rightIsAvailable = true;

    if (currentPrescription.isPrescriptionRetail) {
        leftIsAvailable =
            currentPrescription.isPublishedCLLeft && currentPrescription.buyableCLLeft;
        rightIsAvailable =
            currentPrescription.isPublishedCLRight && currentPrescription.buyableCLRight;
    }

    const initialState: IContactLensForm = {
        leftQuantity: defaultBoxesValue,
        rightQuantity: defaultBoxesValue,
        rxLeftEye: leftIsCurrentProduct,
        rxLeftAdd: leftCl?.attributes?.add?.[0] || '',
        rxLeftAxis: leftCl?.attributes?.axis?.[0] || '',
        rxLeftBc: leftCl?.attributes?.baseCurve?.[0] || '',
        rxLeftColor: leftCl?.attributes?.color?.[0] || '',
        rxLeftCyl: leftCl?.attributes?.cylinder?.[0] || '',
        rxLeftDia: leftCl?.attributes?.diameter?.[0] || '',
        rxLeftPower: leftCl?.attributes?.power?.[0] || '',
        rxLeftDominance: leftCl?.attributes?.dominance?.[0] || '',
        rxRightEye: rightIsCurrentProduct,
        rxRightAdd: rightCl?.attributes?.add?.[0] || '',
        rxRightAxis: rightCl?.attributes?.axis?.[0] || '',
        rxRightBc: rightCl?.attributes?.baseCurve?.[0] || '',
        rxRightColor: rightCl?.attributes?.color?.[0] || '',
        rxRightCyl: rightCl?.attributes?.cylinder?.[0] || '',
        rxRightDia: rightCl?.attributes?.diameter?.[0] || '',
        rxRightPower: rightCl?.attributes?.power?.[0] || '',
        rxRightDominance: rightCl?.attributes?.dominance?.[0] || '',
    };

    const schema = yup.object().shape({
        rxLeftEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxLeftEye, ctx) => rxLeftEye || ctx.parent.rxRightEye
            ),
        rxRightEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxRightEye, ctx) => rxRightEye || ctx.parent.rxLeftEye
            ),
        leftQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxLeftEye ? parseInt(val) > 0 : true
            ),
        rightQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxRightEye ? parseInt(val) > 0 : true
            ),
        rxLeftAdd: yup
            .string()
            .test('invalid', 'rxLeftAdd', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.add?.length ? !!val : true
            ),
        rxLeftAxis: yup
            .string()
            .test('invalid', 'rxLeftAxis', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.axis?.length ? !!val : true
            ),
        rxLeftBc: yup
            .string()
            .test('invalid', 'rxLeftBc', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.baseCurve?.length ? !!val : true
            ),
        rxLeftColor: yup
            .string()
            .test('invalid', 'rxLeftColor', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.color?.length ? !!val : true
            ),
        rxLeftCyl: yup
            .string()
            .test('invalid', 'rxLeftCyl', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.cylinder?.length ? !!val : true
            ),
        rxLeftDia: yup
            .string()
            .test('invalid', 'rxLeftDia', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.diameter?.length ? !!val : true
            ),
        rxLeftPower: yup
            .string()
            .test('invalid', 'rxLeftPower', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.power?.length ? !!val : true
            ),
        rxLeftDominance: yup
            .string()
            .test('invalid', 'rxLeftDominance', (val, ctx) =>
                ctx.parent.rxLeftEye && leftCl?.attributes?.dominance?.length ? !!val : true
            ),
        rxRightAdd: yup
            .string()
            .test('invalid', 'rxRightAdd', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.add?.length ? !!val : true
            ),
        rxRightAxis: yup
            .string()
            .test('invalid', 'rxRightAxis', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.axis?.length ? !!val : true
            ),
        rxRightBc: yup
            .string()
            .test('invalid', 'rxRightBc', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.baseCurve?.length ? !!val : true
            ),
        rxRightColor: yup
            .string()
            .test('invalid', 'rxRightColor', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.color?.length ? !!val : true
            ),
        rxRightCyl: yup
            .string()
            .test('invalid', 'rxRightCyl', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.cylinder?.length ? !!val : true
            ),
        rxRightDia: yup
            .string()
            .test('invalid', 'rxRightDia', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.diameter?.length ? !!val : true
            ),
        rxRightPower: yup
            .string()
            .test('invalid', 'rxRightPower', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.power?.length ? !!val : true
            ),
        rxRightDominance: yup
            .string()
            .test('invalid', 'rxRightDominance', (val, ctx) =>
                ctx.parent.rxRightEye && rightCl?.attributes?.dominance?.length ? !!val : true
            ),
    });

    const methods = useForm<IContactLensForm>({
        resolver: yupResolver(schema),
        defaultValues: initialState,
    });

    const watchLeftEnabled = methods.watch('rxLeftEye');
    const watchRightEnabled = methods.watch('rxRightEye');
    const watchForm = useWatch({ control: methods.control }) as IContactLensForm;

    useOnFormChange({
        ...cleanContactLensForm(watchForm),
        leftIsCurrentProduct,
        rightIsCurrentProduct,
    });

    const onSubmit: SubmitHandler<IContactLensForm> = (data: IContactLensForm) => {
        const { rxLeftEye, leftQuantity, rxRightEye, rightQuantity } = data;

        const submitData: IAddToCartSavedPrescriptionPayload = {
            storeId: config.storeId,
            catalogId: config.catalogId,
            langId: config.langId,
            URL: 'GetCatalogEntryDetailsByID',
            errorViewName: 'LogonAjaxView',
            cache: false,
            action: 'add',
            orderId: '.',
            partType: 'CONTACT_LENSES',
            leftLensItemId: rxLeftEye ? currentPrescription.leftCatentryId : null,
            rightLensItemId: rxRightEye ? currentPrescription.rightCatentryId : null,
            leftPrescriptionId: rxLeftEye ? currentPrescription.prescriptionId : null,
            rightPrescriptionId: rxRightEye ? currentPrescription.prescriptionId : null,
            leftQuantity: rxLeftEye ? leftQuantity : '0',
            rightQuantity: rxRightEye ? rightQuantity : '0',
            isPrescriptionRetail: currentPrescription.isPrescriptionRetail,
            autosetquantity: 'supplyAmount',
        };

        config.onFormSubmit(submitData, true);
        console.log('>>> Saved CL prescription form submit', submitData);
    };

    const onInvalid = error => {
        console.error(error);
    };

    useOnFormSubmit(methods, onSubmit, onInvalid);

    return (
        <FormProvider {...methods}>
            <form
                id={config.prescriptionFormId}
                className={clsx(
                    'contact-lens-prescription-content',
                    isTwoProducts && !isSameProduct && 'different-packs'
                )}
                onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
            >
                <div className="form-container saved-prescription">
                    {/* EYE CHECK */}
                    <div className="eye-column param-column">
                        <div className="param-label eye-label">
                            <label>
                                {getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_LABEL'
                                )}
                            </label>
                        </div>

                        {isSameProduct ? (
                            <>
                                <div className="presc-param-select eye-select right">
                                    <div className="eye-container-checkbox">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    {...methods.register('rxRightEye')}
                                                    size="small"
                                                    inputProps={{
                                                        'aria-label': 'Check OD (right)',
                                                    }}
                                                    defaultChecked
                                                />
                                            }
                                            label={getLabelFallback(
                                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_RIGHT_LONG_LABEL'
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="presc-param-select eye-select left">
                                    <div className="eye-container-checkbox">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    {...methods.register('rxLeftEye')}
                                                    size="small"
                                                    inputProps={{
                                                        'aria-label': 'Check OS (left)',
                                                    }}
                                                    defaultChecked
                                                />
                                            }
                                            label={getLabelFallback(
                                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_LEFT_LONG_LABEL'
                                            )}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="presc-param-select">
                                {getLabelFallback(
                                    rightIsCurrentProduct
                                        ? 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_RIGHT_LONG_LABEL'
                                        : 'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_EYE_TYPE_LEFT_LONG_LABEL'
                                )}
                            </div>
                        )}
                    </div>

                    {/* POWER PARAM */}
                    <FormColumn
                        attribute="power"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_POWER_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftPower',
                                values: addPositiveSign(leftCl?.attributes.power),
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightPower',
                                values: addPositiveSign(rightCl?.attributes.power),
                            },
                            rightIsCurrentProduct
                        )}
                    />

                    {/* BC PARAM */}
                    <FormColumn
                        attribute="bc"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BC_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftBc',
                                values: leftCl?.attributes.baseCurve,
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightBc',
                                values: rightCl?.attributes.baseCurve,
                            },
                            rightIsCurrentProduct
                        )}
                    />

                    {/* DIA PARAM */}
                    <FormColumn
                        attribute="dia"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DIA_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftDia',
                                values: leftCl?.attributes.diameter,
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightDia',
                                values: rightCl?.attributes.diameter,
                            },
                            rightIsCurrentProduct
                        )}
                    />

                    {/* CYL PARAM */}
                    <FormColumn
                        attribute="cyl"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_CYL_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftCyl',
                                values: leftCl?.attributes.cylinder,
                            },
                            leftIsCurrentProduct &&
                                !(
                                    leftCl?.attributes.cylinder?.length === 1 &&
                                    parseFloat(leftCl.attributes.cylinder[0]) === 0
                                )
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightCyl',
                                values: rightCl?.attributes.cylinder,
                            },
                            rightIsCurrentProduct &&
                                !(
                                    rightCl?.attributes.cylinder?.length === 1 &&
                                    parseFloat(rightCl.attributes.cylinder[0]) === 0
                                )
                        )}
                    />

                    {/* AXIS PARAM */}
                    <FormColumn
                        attribute="axis"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_AXIS_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftAxis',
                                values: leftCl?.attributes.axis,
                            },
                            leftIsCurrentProduct &&
                                !(
                                    leftCl?.attributes.axis?.length === 1 &&
                                    parseFloat(leftCl.attributes.axis[0]) === 0
                                ) // shows values only when it is not [0]
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightAxis',
                                values: rightCl?.attributes.axis,
                            },
                            rightIsCurrentProduct &&
                                !(
                                    rightCl?.attributes.axis?.length === 1 &&
                                    parseFloat(rightCl.attributes.axis[0]) === 0
                                )
                        )}
                    />

                    {/* ADD PARAM */}
                    <FormColumn
                        attribute="add"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_ADD_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftAdd',
                                values: leftCl?.attributes.add,
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightAdd',
                                values: rightCl?.attributes.add,
                            },
                            rightIsCurrentProduct
                        )}
                    />

                    {/* COLOR PARAM */}
                    <FormColumn
                        attribute="color"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_COLOR_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftColor',
                                values: leftCl?.attributes.color,
                            },
                            leftIsCurrentProduct &&
                                !(
                                    leftCl?.attributes?.color?.length === 1 &&
                                    leftCl?.attributes?.color[0] == 'Clear'
                                )
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightColor',
                                values: rightCl?.attributes.color,
                            },
                            rightIsCurrentProduct &&
                                !(
                                    rightCl?.attributes?.color?.length === 1 &&
                                    rightCl?.attributes?.color[0] == 'Clear'
                                )
                        )}
                    />

                    {/* DOMINANCE PARAM */}
                    <FormColumn
                        attribute="dominance"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_DOMINANCE_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                name: 'rxLeftDominance',
                                values: leftCl?.attributes.dominance,
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                name: 'rxRightDominance',
                                values: rightCl?.attributes.dominance,
                            },
                            rightIsCurrentProduct
                        )}
                    />

                    <Divider />

                    {/* BOXES PARAM */}
                    <FormColumn
                        attribute="boxes"
                        label={getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_HORIZONTAL_TILE_BOXES_LABEL'
                        )}
                        left={returnValueIf(
                            {
                                enabled: watchLeftEnabled,
                                defaultValue: defaultBoxesValue,
                                name: 'leftQuantity',
                                values: boxesValueOptions,
                                id: 'leftQuantity',
                            },
                            leftIsCurrentProduct
                        )}
                        right={returnValueIf(
                            {
                                enabled: watchRightEnabled,
                                defaultValue: defaultBoxesValue,
                                name: 'rightQuantity',
                                values: boxesValueOptions,
                                id: 'rightQuantity',
                            },
                            rightIsCurrentProduct
                        )}
                    />
                </div>

                {isTwoProducts && !isSameProduct && rightIsAvailable && leftIsAvailable && (
                    <DifferentProductPrescriptionForm
                        boxesValueOptions={boxesValueOptions}
                        defaultBoxesValue={defaultBoxesValue}
                        leftCl={returnValueIf(leftCl, !leftIsCurrentProduct)}
                        rightCl={returnValueIf(rightCl, !rightIsCurrentProduct)}
                    />
                )}
            </form>

            <div className="contact-lens-prescription-errors">
                {Object.keys(methods.formState.errors).length > 0 && (
                    <>
                        Please select your prescription to add contact lenses to cart and un-check
                        box if only one eye is needed.
                    </>
                )}
            </div>
            <div id="prescription-error-cl" className="contact-lens-prescription-errors hide">
                The prescription you selected is not available
            </div>

            {isSameProduct && (
                <CLDiscounts
                    setQuantity={boxes => {
                        methods.setValue('leftQuantity', boxes.toString());
                        methods.setValue('rightQuantity', boxes.toString());
                    }}
                    quantityB={{
                        leftQuantity: methods.getValues().leftQuantity,
                        rightQuantity: methods.getValues().rightQuantity,
                    }}
                />
            )}
        </FormProvider>
    );
};

export default SavedPrescriptionForm;
